import type {Element, Parent, Root as HtmlRoot} from 'hast'
import {visitParents} from 'unist-util-visit-parents'

import {isPositionedNode, type PointWithOffset, type PositionedNode} from '../../syntax-tree/positioned-node'

/**
 * Normalizes all <video> elements in the given HTML AST.
 *
 * In this context, <video> elements are treated as void elements and should not contain any child nodes.
 * This function moves any child content (such as fallback text or elements) outside the <video> element,
 * inserting them immediately after the video node in the parent's children array.
 *
 * Additionally, if the <video> element had children, its position is adjusted to end before the first child,
 * ensuring accurate source mapping and AST structure.
 *
 * The function also sets default properties for video playback:
 *   - controls: true (if not already set)
 *   - preload: 'metadata' (if not already set)
 *   - playsinline: true (if not already set)
 *
 * This normalization ensures that <video> elements are valid, self-contained, and have consistent playback behavior.
 *
 * @param tree The HTML AST root to normalize.
 * @param markdownSource Optional original markdown source to detect explicit boolean attribute usage.
 */
export function normalizeVideoElements(tree: HtmlRoot, markdownSource?: string): void {
  visitParents(tree, 'element', (node, ancestors) => {
    if (node.tagName !== 'video' || !Array.isArray(node.children)) return

    const parent = ancestors[ancestors.length - 1] as (Parent & {children?: Parent['children']}) | undefined
    if (!parent || !Array.isArray(parent.children)) return

    const parentIndex = parent.children.indexOf(node as unknown as Parent['children'][number])
    if (parentIndex === -1) return

    const parentIsParagraph = parent.type === 'element' && (parent as Element).tagName === 'p'
    const grandparent = parentIsParagraph
      ? (ancestors[ancestors.length - 2] as (Parent & {children?: Parent['children']}) | undefined)
      : undefined
    const container = grandparent && Array.isArray(grandparent.children) ? grandparent : parent
    if (!Array.isArray(container.children)) return

    const containerIndex = container === parent ? parentIndex : container.children.indexOf(parent as never)
    if (containerIndex === -1) return

    const element = node
    const properties = (element.properties ??= {})

    let playsInlineSnippet: string | undefined
    if (
      markdownSource &&
      element.position &&
      typeof element.position.start?.offset === 'number' &&
      typeof element.position.end?.offset === 'number'
    ) {
      playsInlineSnippet = markdownSource.slice(element.position.start.offset, element.position.end.offset)
    }

    const controlsValue = properties.controls as unknown
    if (controlsValue === undefined) {
      properties.controls = true
    } else if (controlsValue === 'false') {
      properties.controls = false
    } else if (controlsValue === 'true') {
      properties.controls = true
    }

    if (properties.preload === undefined) properties.preload = 'metadata'

    let playsInlineKey: 'playsinline' | 'playsInline' = 'playsinline'
    let playsInlineValue = properties[playsInlineKey] as unknown
    if (playsInlineValue === undefined && Object.prototype.hasOwnProperty.call(properties, 'playsInline')) {
      playsInlineKey = 'playsInline'
      playsInlineValue = properties[playsInlineKey] as unknown
    }

    const synchronizePlaysInline = (value: boolean) => {
      properties.playsinline = value
      properties.playsInline = value
    }

    const explicitPlaysInlineFalse =
      typeof playsInlineSnippet === 'string' && /playsinline\s*=\s*['"]?false['"]?/i.test(playsInlineSnippet)
    const explicitPlaysInlineTrue =
      typeof playsInlineSnippet === 'string' && /playsinline\s*=\s*['"]?true['"]?/i.test(playsInlineSnippet)

    if (playsInlineValue === undefined) {
      if (explicitPlaysInlineFalse) {
        synchronizePlaysInline(false)
      } else if (explicitPlaysInlineTrue) {
        synchronizePlaysInline(true)
      } else {
        synchronizePlaysInline(true)
      }
    } else if (playsInlineValue === 'false' || playsInlineValue === false) {
      synchronizePlaysInline(false)
    } else if (playsInlineValue === 'true' || playsInlineValue === true) {
      synchronizePlaysInline(true)
    }
    const childrenToAppend: typeof element.children = []

    for (const child of element.children) {
      if (child.type === 'text' && child.value.trim() === '') continue
      if (child.type === 'element' && 'properties' in child && child.properties?.dataDelimiter === 'htmlTag') continue
      childrenToAppend.push(child)
    }

    element.children = []

    if (childrenToAppend.length > 0) {
      const candidates = childrenToAppend
        .filter((child): child is Element & PositionedNode => isPositionedNode(child))
        .map(child => ({child, start: child.position.start as PointWithOffset}))

      const earliestCandidate = candidates.reduce<{child: Element; start: PointWithOffset} | undefined>(
        (earliest, candidate) => {
          if (!earliest) return candidate
          return candidate.start.offset < earliest.start.offset ? candidate : earliest
        },
        undefined,
      )

      if (element.position && earliestCandidate) {
        element.position = {
          ...element.position,
          end: {...earliestCandidate.start},
        }
      }

      container.children.splice(containerIndex + 1, 0, ...childrenToAppend)
    }

    if (parentIsParagraph && grandparent && Array.isArray(grandparent.children)) {
      grandparent.children.splice(containerIndex, 1, element as never)
    }
  })
}
